<template>
  <section class="list">
    <div v-if="loading" class="loading-wrapper">
      <p class="loading-text">Cargando categorías...</p>
    </div>
    <div v-else class="row">
      <article
        class="card"
        v-for="category in categories"
        :key="category.code"
        :class="{ 'disease-card': category.is_disease }"
        @click="selectItem(category)"
      >
        <img
          :src="getImageUrl(category.photo_url)"
          class="card-image"
          :alt="category.name"
          @error="onImgError($event)"
        />
        <h3>{{ category.name }}</h3>
        <span v-if="category.is_disease" class="disease-badge">Enfermedad</span>
      </article>
    </div>
  </section>
</template>

<script>
import defaultImage from "@/assets/img/formato/formato.jpeg";
import { cacheGet, cacheSet } from "@/services/offlineService";

export default {
  name: "ListCategories",

  props: {
    finca: {
      type: Object,
      required: true,
    },
    zona: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      categories: [],
      loading: false,
      defaultImage,
    };
  },

  watch: {
    mode(newVal) {
      if (newVal) this.fetchCategories();
    },
    finca: {
      handler(newVal) {
        if (newVal && newVal.id) this.fetchCategories();
      },
      deep: true,
    },
  },

  mounted() {
    this.fetchCategories();
  },

  methods: {
    getImageUrl(photoUrl) {
      if (!photoUrl) {
        return defaultImage;
      }

      const normalizedUrl = String(photoUrl).trim();
      if (
        normalizedUrl.startsWith("data:") ||
        normalizedUrl.startsWith("blob:")
      ) {
        return normalizedUrl;
      }

      if (
        normalizedUrl.startsWith("http://") ||
        normalizedUrl.startsWith("https://")
      ) {
        try {
          const parsedUrl = new URL(normalizedUrl);
          if (
            ["localhost", "127.0.0.1", "0.0.0.0"].includes(
              parsedUrl.hostname,
            ) ||
            parsedUrl.hostname.startsWith("10.0.2.")
          ) {
            return `${this.$hostname}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
          }
          if (parsedUrl.protocol === "http:") {
            parsedUrl.protocol = "https:";
          }
          return parsedUrl.toString();
        } catch {
          return normalizedUrl.replace(/^http:\/\//i, "https://");
        }
      }

      const path = normalizedUrl.startsWith("/")
        ? normalizedUrl
        : `/${normalizedUrl.replace(/^\/+/, "")}`;
      return `${this.$hostname}${path}`;
    },

    async fetchCategories() {
      this.loading = true;
      // Build query string with mode and/or farmland_id
      const queryParams = [];
      if (this.mode) {
        queryParams.push(`mode=${this.mode}`);
      }
      if (this.finca && this.finca.id) {
        queryParams.push(`farmland=${this.finca.id}`);
      }
      const params = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
      const url = `${this.$hostname}/categories/${params}`;

      try {
        // Network-first: evita mostrar listas antiguas tras cambios en admin.
        const response = await this.$http.get(url);
        const sorted = this.sortCategories(response.data);
        this.categories = sorted;
        await cacheSet(url, sorted);

        // Auto-select if only one category available
        if (this.categories.length === 1) {
          this.$nextTick(() => this.selectItem(this.categories[0]));
        }
      } catch (e) {
        try {
          const cached = await cacheGet(url);
          if (cached) {
            const sorted = this.sortCategories(cached);
            this.categories = sorted;
            // Auto-select if only one category in cache
            if (this.categories.length === 1) {
              this.$nextTick(() => this.selectItem(this.categories[0]));
            }
            return;
          }
        } catch (cacheError) {
          console.error("Error al leer caché de categorías:", cacheError);
        }

        console.error("Error al cargar categorías:", e);
        this.categories = [];
      } finally {
        this.loading = false;
      }
    },

    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },

    getCategoryOrderWeight(category) {
      const code = String((category && category.code) || "").toUpperCase();
      const normalizedName = this.normalizeText(category && category.name);

      if (code === "PRIMERA") return 10;
      if (code === "SEGUNDA") return 20;
      if (code === "INDUSTRIA") return 30;
      if (code === "MALAS" || normalizedName === "malas") return 31;

      if (category && category.is_disease) return 100;
      return 50;
    },

    sortCategories(categories) {
      return (categories || []).slice().sort((a, b) => {
        const orderA = this.getCategoryOrderWeight(a);
        const orderB = this.getCategoryOrderWeight(b);
        if (orderA !== orderB) return orderA - orderB;

        const nameA = String((a && a.name) || "");
        const nameB = String((b && b.name) || "");
        const nameSort = nameA.localeCompare(nameB, "es", {
          numeric: true,
          sensitivity: "base",
        });
        if (nameSort !== 0) return nameSort;

        const codeA = String((a && a.code) || "");
        const codeB = String((b && b.code) || "");
        return codeA.localeCompare(codeB, "es", {
          numeric: true,
          sensitivity: "base",
        });
      });
    },

    onImgError(event) {
      event.target.src = this.defaultImage;
    },

    selectItem(category) {
      console.log(`Categoría seleccionada: ${category.code}`);
      this.$root.$emit("category", category);
    },
  },
};
</script>

<style scoped>
.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
}

.loading-text {
  color: #666;
  font-size: 0.95em;
}

.disease-card {
  border: 2px solid #e57373;
}

.disease-badge {
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  background: #e57373;
  color: #fff;
  border-radius: 10px;
  font-size: 0.72em;
  font-weight: 600;
}
</style>
